import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '~/components';
import { Button } from '~/components';
import { useProjectContext, usePortfolioContext } from '~/hooks';
import { useForm, usePageType } from '~/hooks';
import { ProjectInvestorQueue } from '~/types/project';
import InvestorInvitationForm from './InvestorInvitationForm';
import FeeForms from './FeeForms';
import useAddOrUpdateInvestor from '~/mutations/admin/useAddOrUpdateInvestor';
import { withForm } from '~/utils/withForm';
import { FeeUnits } from '~/constants';

interface AddEditModalProps {
  investorRow?: ProjectInvestorQueue;
  closeModal: () => void;
}

export interface InvestorInvitationFormState {
  investorId: string;
  investorName?: string;
  nrsFee: number | null;
  nrsFeeUnit: string;
  buyerStorageFee?: number | null;
  buyerStorageFeeUnit?: string;
  sellerFee?: number | null;
  sellerFeeUnit?: string;
  sellerStorageFee?: number | null;
  sellerStorageFeeUnit?: string;
}

const defaults: InvestorInvitationFormState = {
  investorId: '',
  investorName: '',
  nrsFee: null,
  nrsFeeUnit: '',
  buyerStorageFee: null,
  buyerStorageFeeUnit: '',
  sellerFee: null,
  sellerFeeUnit: '',
  sellerStorageFee: null,
  sellerStorageFeeUnit: '',
};

const AddEditInvestorModal = withForm({ defaults }, ({
  closeModal,
  investorRow,
}: AddEditModalProps) => {
  const pageType = usePageType();
  const { fetchProject } = useProjectContext();
  const { updatePortfolioValue } = usePortfolioContext();
  const { formState } = useForm();
  const [noFeesSelected, setHasNoFeesSelected] = useState<boolean>(true);
  const [hasUnitErrors, setHasUnitErrors] = useState<boolean>(false);
  const isEditing = Boolean(investorRow);

  //This function populates the edit form with the existing values of the investor
  const updateDefaults = (
    defaults: Record<string, any>,
    investorRow?: ProjectInvestorQueue,
  ) => {
    Object.assign(defaults, investorRow ? {
      investorName: investorRow.investor?.company?.name,
      // this is a special case because nrs_fee is required in the db
      nrsFee: investorRow.nrsFee === 0 ? null : investorRow.nrsFee,
      nrsFeeUnit: investorRow.nrsFee === 0 ? null : investorRow.nrsFeeUnit,
      buyerStorageFee: investorRow.buyerStorageFee,
      buyerStorageFeeUnit: investorRow.buyerStorageFeeUnit,
      sellerFee: investorRow.sellerFee,
      sellerFeeUnit: investorRow.sellerFeeUnit,
      sellerStorageFee: investorRow.sellerStorageFee,
      sellerStorageFeeUnit: investorRow.sellerStorageFeeUnit,
    } : {
      investorId: '',
      investorName: '',
      nrsFee: null,
      nrsFeeUnit: '',
      buyerStorageFee: null,
      buyerStorageFeeUnit: '',
      sellerFee: null,
      sellerFeeUnit: '',
      sellerStorageFee: null,
      sellerStorageFeeUnit: '',
    });
  };
  //Send only the values that have been added or changed in the form
  //Include special cases if form values are cleared and become undefined
  const filteredFormState = Object.keys(formState).reduce((acc, key) => {
    let value = formState[key as keyof InvestorInvitationFormState];

    // Special case for nrsFee: set to 0 if undefined
    if (key === 'nrsFee' && (value === undefined || value === null || value === '')) {
      value = 0;
    }

    // Special case for nrsFeeUnit: set to PERCENT_OF_BUDGET if undefined
    if (key === 'nrsFeeUnit' && (value === undefined || value === null || value === '')) {
      value = FeeUnits.PERCENT_OF_BUDGET;
    }

    // Other fee fields: set to null if undefined
    if (key.includes('Fee') && key !== 'nrsFee' && (value === undefined || value === 0)) {
      value = null;
    }

    // Always include nrsFee and nrsFeeUnit, or if value differs from default
    if (key === 'nrsFee' || key === 'nrsFeeUnit' || value !== defaults[key as keyof InvestorInvitationFormState]) {
      acc[key as keyof InvestorInvitationFormState] = value;
    }

    return acc;
  }, {} as Partial<InvestorInvitationFormState>);

  if (isEditing) {
    filteredFormState.investorId = investorRow?.investor?.id;
    updateDefaults(defaults, investorRow);
  }

  const handleCloseModal = () => {
    //Reset the form state to the default values
    updateDefaults(defaults);
    closeModal();
  };

  const { mutateAsync: addOrUpdateInvestor, status: addOrUpdateInvestorStatus } = useAddOrUpdateInvestor({ isEditing, data: filteredFormState });

  const handleAddUpdateInvestor = async () => {
    try {
      const result = await addOrUpdateInvestor({ isEditing, data: filteredFormState });

      if (pageType === 'project') {
        fetchProject();
      } else if (pageType === 'portfolio') {
        updatePortfolioValue('projects', result, { doSave: false });
      }

      handleCloseModal();
      toast.success(
        `${isEditing
          ? `Updating investor in the ${pageType}'s queue`
          : `Adding investor to the ${pageType}'s queue`}`,
        { duration: 5000 }
      );
    } catch (error) {
      console.error(error);
      toast.error(
        `${isEditing
          ? `An error occurred while updating investor in the ${pageType}'s queue`
          : `An error occurred while adding investor to the ${pageType}'s queue.`}`,
        { duration: 5000 }
      );
    }
  };

  useEffect(() => {

    const feeData = [
      { feeType: 'nrsFee', fee: formState.nrsFee ?? 0, unitType: 'nrsFeeUnit', unit: formState.nrsFeeUnit ?? '' },
      { feeType: 'buyerStorageFee', fee: formState.buyerStorageFee ?? 0, unitType: 'buyerStorageFeeUnit', unit: formState.buyerStorageFeeUnit ?? '' },
      { feeType: 'sellerFee', fee: formState.sellerFee ?? 0, unitType: 'sellerFeeUnit', unit: formState.sellerFeeUnit ?? '' },
      { feeType: 'sellerStorageFee', fee: formState.sellerStorageFee ?? 0, unitType: 'sellerStorageFeeUnit', unit: formState.sellerStorageFeeUnit ?? '' },
    ];

    //If there are no fees added, set error
    const feesAdded = feeData.filter(({ fee }) => fee !== 0);
    setHasNoFeesSelected(Boolean(feesAdded.length === 0));

    let unitError: boolean = false;

    feeData.forEach(({ feeType, fee, unit }) => {
      if (feeType !== 'nrsFee') {
        // If fee has value but its unit is empty, set error 
        if (fee !== 0 && unit === '') {
          unitError = true;
        }
      }
    });

    //Set state outside of loop to avoid multiple re-renders
    setHasUnitErrors(unitError);
  }, [formState]);

  const hasFeeErrors = isEditing ? noFeesSelected : !formState.investorId || noFeesSelected;

  return (
    <Modal closeModal={addOrUpdateInvestorStatus === 'loading' ? undefined : handleCloseModal}>
      <Modal.Title>
        {isEditing ? 'Edit investor in queue' : 'Add investor to queue'}
      </Modal.Title>
      <Modal.Content>
        <InvestorInvitationForm investorRow={investorRow} />
        <FeeForms />
      </Modal.Content>
      <Modal.Footer>
        <Button
          primary
          outlined
          onClick={handleCloseModal}
        >
          Cancel
        </Button>
        <Button
          primary
          loading={addOrUpdateInvestorStatus === 'loading'}
          disabled={addOrUpdateInvestorStatus === 'loading' || hasFeeErrors || hasUnitErrors}
          onClick={() => handleAddUpdateInvestor()}
        >
          {isEditing ? 'Save changes' : 'Add investor to queue'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
});

export default AddEditInvestorModal;